<template>
	<FloatingPanel
		:title="labels.title"
		@close="close"
	>
		<template #subtitle>
			<p>
				<span v-html="labels.body" />
			</p>
		</template>
		<template #body>
			<div
				v-if="isTimeShiftEnabled"
				class="timeshift-wrapper"
			>
				<div class="form-group">
					<div class="h-disp-flex mb-2">
						<span class="mr-2 mb-2">{{ labels.timeShift.title }}</span>
						<LabelWithTooltip
							:label="transUcfirst('one.tag_timeshift_info')"
							:wrapper-class="null"
							:tooltip-class="`icon-tooltip-w200`"
						>
							<span class="h-cursor-pointer">
								<Icon
									icon-name="c-info"
									size="lg"
								/>
							</span>
						</LabelWithTooltip>
					</div>
					<div class="h-disp-flex">
						<button
							class="button button--switch h-width-full"
							:disabled="formDisabled"
							:class="{selected: isTimeShiftBackSelected}"
							@click="handleTimeShiftBackwardClick"
						>
							{{ trans('commons.back') }}
						</button>
						<button
							class="button button--switch h-width-full"
							:disabled="formDisabled"
							:class="{selected: isTimeShiftForthSelected}"
							@click="handleTimeShiftForthClick"
						>
							{{ trans('commons.forth') }}
						</button>
					</div>
				</div>
				<div class="form-group">
					<FormField
						:label="labels.timeShift.amount"
						:error="formErrors.timeShiftOffset"
					>
						<TimeInput
							v-model="timeShiftOffset"
							:is-valid="!formErrors.timeShiftOffset"
							:disabled="formDisabled"
						>
							<template #right>
								<Icon
									icon-name="clock"
									size="lg"
								/>
							</template>
						</TimeInput>
					</FormField>
				</div>
			</div>
		</template>

		<template
			v-if="!showConfirmation"
			#footer
		>
			<div class="form-actions">
				<DiscardButton
					:disabled="!hasChanges"
					@click.native="formReset"
				/>
				<button
					class="button button--primary"
					data-qa="save-tag-button"
					:disabled="!canSave"
					@click="handleSave"
				>
					{{ transUcfirst('commons.save') }}
				</button>
			</div>
		</template>

		<template
			v-if="showConfirmation"
			#footer-overlay
		>
			<div class="form-confirm-box">
				<p class="description">
					{{ transUcfirst('one.tag_timeshift_overflow_warning') }}
				</p>
				<div class="form-actions">
					<button
						class="button w-full button--secondary"
						@click="handleDiscard"
					>
						{{ transUcfirst('commons.cancel') }}
					</button>
					<button
						class="button w-full button--primary"
						@click="handleConfirmSave"
					>
						{{ transUcfirst('commons.save_anyway') }}
					</button>
				</div>
			</div>
		</template>
	</FloatingPanel>
</template>

<script>
import FloatingPanel from '../Panels/FloatingPanel';
import { mapActions, mapGetters } from 'vuex';
import FormField from '../Form/FormField';
import DiscardButton from '../../../components/buttons/DiscardButton.vue';
import TimeInput from '../../../components/TimeInput/TimeInput';

const TIME_SHIFT_DIR_BACK = 'back';
const TIME_SHIFT_DIR_FORTH = 'forth';

const SAVE_CONFIRMATION_NONE = 'none';
const SAVE_CONFIRMATION_REQUIRED = 'required';
const SAVE_CONFIRMATION_CONFIRMED = 'confirmed';

export default {
	name:       'TimeshiftEdit',
	components: { FormField, FloatingPanel, DiscardButton, TimeInput },
	props:      {
	},
	data() {
		return {
			timeShiftDirection: TIME_SHIFT_DIR_BACK,
			timeShiftOffset:    0,
			saveConfirmStatus:  SAVE_CONFIRMATION_NONE,
			formErrors:         {
				timeShiftOffset: null,
			},
		};
	},
	computed: {
		...mapGetters({
			isVod:                'event/isVod',
			event:                'event/event',
			eventParticipants:    'event/participants',
			sportName:            'event/sportName',
			sportParticipantType: 'event/participantType',
			areAllTagsSelected:   'eventTagEdit/areAllTagsSelected',
			eventTags:            'eventTagEdit/selectedTags',
			isForEvent:           'tags/isForEvent',
			isForParticipant:     'tags/isForParticipant',
			isForTiming:          'tags/isForTiming',
			eventTargetTags:      'tags/eventTargetTags',
			participantTags:      'tags/participantTags',
			timingTags:           'tags/timingTags',
			isPlayerCompetition:  'event/isPlayerCompetition',
		}),
		labels() {
			return {
				title: this.$I18n.trans('commons.type_edit', {
					type: this.$I18n.trans_choice('one.tag', 2),
				}),
				body:      this.$I18n.trans('one.you_have_selected_count', { num: this.tagCount, type: this.$I18n.trans_choice('one.tag', this.tagCount) }),
				item:      this.$I18n.trans_choice('one.tag', 2),
				timeShift: {
					title:  this.transUcfirst('one.tag_timeshift_title'),
					amount: this.transUcfirst('one.tag_timeshift_amount'),
				},
			};
		},
		timeShiftDirectionSign() {
			return this.timeShiftDirection === TIME_SHIFT_DIR_FORTH ? 1 : -1;
		},
		tagCount() {
			return this.eventTags.length;
		},
		isTimeShiftEnabled() {
			return true;
		},
		isTimeShiftBackSelected() {
			return this.timeShiftDirection === TIME_SHIFT_DIR_BACK;
		},
		isTimeShiftForthSelected() {
			return this.timeShiftDirection === TIME_SHIFT_DIR_FORTH;
		},
		hasFormErrors() {
			return Object.values(this.formErrors).find(err => !!err);
		},
		canSave() {
			return !this.hasFormErrors && this.hasTimeShiftChanges;
		},
		hasChanges() {
			return this.hasTimeShiftChanges;
		},
		hasTimeShiftChanges() {
			return !!this.timeShiftOffset;
		},
		showConfirmation() {
			// return true;
			return this.saveConfirmStatus === SAVE_CONFIRMATION_REQUIRED;
		},
		formDisabled() {
			return this.showConfirmation;
		},
		footerClasses() {
			return this.showConfirmation ? 'form-confirmation-ct' : '';
		},
	},
	watch: {
		timeShiftOffset(val) {
			this.validateTimeShiftOffset(val);
		},
	},
	methods: {
		...mapActions('eventTagEdit', [
			'editEventTag',
			'bulkEditEventTags',
			'timeshift',
			'clearSelection',
		]),
		close() {
			this.clearSelection();
		},
		handleCancel() {
			this.clearSelection();
		},
		handleSave() {
			if (this.saveConfirmStatus !== SAVE_CONFIRMATION_CONFIRMED && !this.validateTimeShiftOverflow()) {
				this.saveConfirmStatus = SAVE_CONFIRMATION_REQUIRED;
				return;
			}

			const data = {
				timeShift:    null,
				selectedTags: this.eventTags,
			};

			if (this.hasTimeShiftChanges) {
				data.timeShift = {
					offset: this.timeShiftOffset * this.timeShiftDirectionSign,
				};
			}

			this.timeshift(data);
			this.saveConfirmStatus = SAVE_CONFIRMATION_NONE;
		},
		handleDiscard() {
			this.saveConfirmStatus = SAVE_CONFIRMATION_NONE;
		},
		handleConfirmSave() {
			this.saveConfirmStatus = SAVE_CONFIRMATION_CONFIRMED;
			this.handleSave();
		},
		handleTimeShiftBackwardClick() {
			this.timeShiftDirection = TIME_SHIFT_DIR_BACK;
		},
		handleTimeShiftForthClick() {
			this.timeShiftDirection = TIME_SHIFT_DIR_FORTH;
		},
		validateTimeShiftOffset(value) {
			let err = null;
			if (!value) {
				err = this.trans('validation.time_format');
			}
			this.formErrors.timeShiftOffset = err;

			return !!err;
		},
		validateTimeShiftOverflow() {
			if (!this.eventTags.length) {
				return true;
			}

			// sort the tags in ascending order by timestamp
			const orderedTags = this.eventTags.sort((a, b) => a.timestamp === b.timestamp ? 0 : (a.timestamp < b.timestamp ? 1 : -1));
			const offset = this.timeShiftOffset * this.timeShiftDirectionSign;

			// check if any of the tags are outside the bounds of the event
			const invalidTag = [
				orderedTags[0],
				orderedTags[orderedTags.length - 1],
			].find((tag) => {
				return offset + tag.endTimestamp > this.event.duration ||
								offset + tag.startTimestamp < 0;
			});

			return !invalidTag;
		},
	},
};
</script>

<style lang="scss" scoped>

.timeshift-wrapper {
	margin-top: 48px;
}

</style>
